import type { LogEntry } from "../API/Models";

/**
 * Raw payload pushed on the SignalR "activity" channel : one per `SystemLogService` write on the
 * server (see CMS.Core SystemLogService). Shapes onto {@link LogEntry} so the live feed and the
 * polled `/api/logs` list share one rendering path on the dashboard.
 */
export type ActivityPayload = {
	id?: string | null;
	type?: string | null;
	level?: string | null;
	message?: string | null;
	details?: string | null;
	user?: string | null;
	/** Courriel de l'acteur : la clé STABLE de résolution d'identité (avatar, profil) ; le nom
	 *  d'affichage change, le courriel non. Le serveur l'envoie sur chaque écriture. */
	userEmail?: string | null;
	tags?: string[] | null;
	/** Structured properties (normalised `action`, schemaKey, contentId, title…) : the server fans
	 *  them out so live rows carry the same action badge as the persisted ones. */
	properties?: { [key: string]: string } | null;
	createdAt?: string | null;
};

/** Map a live activity payload to the LogEntry shape the feed renders. */
export const activityToLogEntry = (payload: ActivityPayload): LogEntry => ({
	id: payload.id ?? undefined,
	type: payload.type ?? "",
	level: payload.level ?? "Information",
	message: payload.message ?? "",
	details: payload.details ?? "",
	user: payload.user ?? "SYSTEM",
	userEmail: payload.userEmail ?? undefined,
	tags: payload.tags ?? [],
	properties: payload.properties ?? undefined,
	createdAt: payload.createdAt ?? undefined
});

/** Stable identity for a log entry : its id, or a content signature when the id is missing. */
const entryKey = (entry: LogEntry): string =>
	entry.id && entry.id.length > 0 ? entry.id : `${entry.createdAt ?? ""}|${entry.message ?? ""}`;

/**
 * Merge one freshly-streamed entry into the existing feed: prepend it (newest first), drop any
 * duplicate already present (by id/signature), and cap the list length so the feed can stream
 * indefinitely without growing unbounded. Pure : safe to unit test and to call from a setState
 * updater.
 */
export const mergeActivityEntry = (existing: LogEntry[], incoming: LogEntry, cap: number): LogEntry[] => {
	const key = entryKey(incoming);
	const deduped = existing.filter((entry) => entryKey(entry) !== key);
	const next = [incoming, ...deduped];
	return cap > 0 && next.length > cap ? next.slice(0, cap) : next;
};

/**
 * Whether an event is machine-generated (the recurring ForexAPI sync, feed auto-publish, or any
 * SYSTEM/unattributed write) rather than a human action. Automated events render demoted (calm,
 * muted row); human actions are emphasised. The exchange-rate mirror writes under the technical
 * user "exchange-rate-sync", so those count as automated too.
 */
export const isAutomated = (entry: LogEntry): boolean => {
	const user = (entry.user ?? "").trim().toUpperCase();
	const type = (entry.type ?? "").toLowerCase();
	return user === "" || user === "SYSTEM" || user === "EXCHANGE-RATE-SYNC" || type === "forex-sync" || type === "content-auto-publish";
};

/**
 * Bruit à exclure du DASHBOARD (les deux panneaux de modifications) : jamais du modal « Voir tout »,
 * qui montre tout. Règles métier exactes :
 *  - `forex-sync` : le battement 30 min du ForexAPI, écrit que les valeurs aient changé ou non → bruit pur ;
 *  - les entrées techniques du miroir de taux (user « exchange-rate-sync ») → bruit du pipeline,
 *    SAUF `forex-rates`, qui est LE vrai changement de valeurs et doit rester visible sur le dashboard.
 */
export const isDashboardNoise = (entry: LogEntry): boolean => {
	const type = (entry.type ?? "").trim().toLowerCase();
	if (type === "forex-sync") return true;
	const user = (entry.user ?? "").trim().toLowerCase();
	if (user === "exchange-rate-sync" && type !== "forex-rates") return true;
	return false;
};
